import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, FileText, Shield, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { SafeHtml } from "@/components/SafeHtml";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LegalPage() {
  const location = useLocation();
  // Determine page type from URL path
  const pageType = location.pathname === "/polityka-prywatnosci" ? "polityka-prywatnosci" : "regulamin";
  
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageConfig = {
    "regulamin": {
      title: "Regulamin",
      icon: FileText,
      description: "Zasady korzystania z platformy Epidemos"
    },
    "polityka-prywatnosci": {
      title: "Polityka prywatności",
      icon: Shield,
      description: "Informacje o przetwarzaniu danych osobowych"
    }
  };

  const config = pageConfig[pageType] || pageConfig["regulamin"];

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/public/legal/${pageType}`);
        setContent(res.data);
      } catch (err) {
        console.error("Błąd pobierania treści:", err);
        setError("Nie udało się załadować treści. Spróbuj ponownie później.");
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [pageType]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header onLogin={() => window.location.href = "/login"} />

      {/* Hero */}
      <section className="pt-24 pb-12 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6">
            <config.icon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{config.title}</h1>
          <p className="text-blue-100 text-lg">{config.description}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-blue-600 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Powrót na stronę główną
          </Link>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500 mb-4">{error}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Spróbuj ponownie
              </Button>
            </div>
          ) : content ? (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 sm:p-12">
              {content.last_updated && (
                <p className="text-sm text-slate-400 mb-6">
                  Ostatnia aktualizacja: {new Date(content.last_updated).toLocaleDateString('pl-PL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
              <SafeHtml
                className="prose prose-slate max-w-none prose-headings:font-bold prose-headings:text-slate-900 prose-h1:text-2xl prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-lg prose-p:text-slate-600 prose-p:leading-relaxed prose-li:text-slate-600 prose-strong:text-slate-900 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                html={content.content}
              />
            </div>
          ) : (
            <div className="text-center py-20">
              <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Treść w przygotowaniu</h2>
              <p className="text-slate-500">Ta strona jest obecnie aktualizowana. Zapraszamy wkrótce.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
